import { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, Search, AlertTriangle, CheckCircle, Clock, XCircle, Download, FileText, Calendar, User } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const ResponsablePresences = ({ user }) => {
  const [presences, setPresences] = useState([]);
  const [stagiaires, setStagiaires] = useState([]);
  const [centreNom, setCentreNom] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  
  // Pour le téléchargement
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedDateFin, setSelectedDateFin] = useState('');
  const [selectedStagiaire, setSelectedStagiaire] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [presRes, centreRes, stagRes] = await Promise.all([
        axios.get('/responsable/presences'),
        axios.get('/responsable/mon-centre'),
        axios.get('/responsable/stagiaires')
      ]);
      setPresences(presRes.data || []);
      setCentreNom(centreRes.data?.nom || '');
      setStagiaires(stagRes.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (statut) => {
    switch (statut) {
      case 'present':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'absent':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'retard':
        return <Clock className="h-4 w-4 text-orange-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (statut) => {
    const colors = {
      present: 'bg-green-100 text-green-700',
      absent: 'bg-red-100 text-red-700',
      retard: 'bg-orange-100 text-orange-700'
    };
    const labels = {
      present: 'Présent',
      absent: 'Absent',
      retard: 'Retard'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[statut] || 'bg-slate-100 text-slate-700'}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  // Téléchargement feuille collective PDF
  const downloadPresenceCollective = async () => {
    try {
      toast.info('Génération du PDF...');
      let url = `/pdf/presence-collective/${selectedDate}`;
      if (selectedDateFin) {
        url += `?date_fin=${selectedDateFin}`;
      }
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const filename = `presence_collective_${centreNom}_${selectedDate}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Feuille de présence collective téléchargée');
    } catch (error) {
      toast.error('Erreur lors de la génération');
    }
  };

  // Téléchargement feuille collective Excel
  const downloadPresenceCollectiveExcel = async () => {
    try {
      toast.info('Génération du fichier Excel...');
      const response = await axios.get(`/excel/presence-collective/${selectedDate}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedDate}_${centreNom}_presence_collective.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Fichier Excel téléchargé');
    } catch (error) {
      toast.error('Erreur lors de la génération Excel');
    }
  };

  // Téléchargement feuille individuelle PDF
  const downloadPresenceIndividuelle = async () => {
    if (!selectedStagiaire) {
      toast.warning('Veuillez sélectionner un stagiaire');
      return;
    }
    try {
      toast.info('Génération du PDF...');
      const response = await axios.get(`/pdf/feuille-presence/${selectedStagiaire}`, { responseType: 'blob' });
      const stag = stagiaires.find(s => s.user_id === selectedStagiaire);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stag?.user_info?.nom || 'stagiaire'}_${stag?.user_info?.prenom || ''}_presence.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Feuille de présence individuelle téléchargée');
    } catch (error) {
      toast.error('Erreur lors de la génération');
    }
  };

  // Téléchargement feuille individuelle Excel
  const downloadPresenceIndividuelleExcel = async () => {
    if (!selectedStagiaire) {
      toast.warning('Veuillez sélectionner un stagiaire');
      return;
    }
    try {
      toast.info('Génération du fichier Excel...');
      const response = await axios.get(`/excel/feuille-presence/${selectedStagiaire}`, { responseType: 'blob' });
      const stag = stagiaires.find(s => s.user_id === selectedStagiaire);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stag?.user_info?.nom || 'stagiaire'}_${stag?.user_info?.prenom || ''}_presence.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Fichier Excel téléchargé');
    } catch (error) {
      toast.error('Erreur lors de la génération Excel');
    }
  };

  const filteredPresences = presences.filter(p => {
    const fullName = `${p.stagiaire_info?.prenom || ''} ${p.stagiaire_info?.nom || ''}`.toLowerCase();
    const matchSearch = fullName.includes(searchTerm.toLowerCase());
    const matchFilter = filterStatut === 'all' || p.statut === filterStatut;
    return matchSearch && matchFilter;
  });

  // Stats
  const stats = {
    total: presences.length,
    presents: presences.filter(p => p.statut === 'present').length,
    absents: presences.filter(p => p.statut === 'absent').length,
    retards: presences.filter(p => p.statut === 'retard').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="responsable-presences">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-[#0F172A] mb-2">
            Présences - Centre {centreNom}
          </h1>
          <p className="text-slate-600">Suivi et téléchargement des feuilles de présence</p>
        </div>
      </div>

      {/* Section Téléchargement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feuille Collective */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">Feuille de Présence Collective</h3>
              <p className="text-sm text-slate-500">Tous les stagiaires du centre {centreNom}</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Date de début</label>
                <Input 
                  type="date" 
                  value={selectedDate} 
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Date de fin (optionnel)</label>
                <Input 
                  type="date" 
                  value={selectedDateFin} 
                  onChange={(e) => setSelectedDateFin(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              {selectedDateFin ? `Période du ${selectedDate} au ${selectedDateFin}` : 'Feuille sur 5 jours à partir de la date de début'}
            </p>
            <div className="flex gap-2">
              <Button onClick={downloadPresenceCollective} className="flex-1 bg-[#8DC63F] hover:bg-[#7db336]">
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button onClick={downloadPresenceCollectiveExcel} variant="outline" className="flex-1 border-green-500 text-green-600 hover:bg-green-50">
                <Download className="h-4 w-4 mr-2" />
                Excel
              </Button>
            </div>
          </div>
        </div>

        {/* Feuille Individuelle */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <User className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">Feuille de Présence Individuelle</h3>
              <p className="text-sm text-slate-500">Un stagiaire spécifique</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Sélectionner un stagiaire</label>
              <select
                value={selectedStagiaire}
                onChange={(e) => setSelectedStagiaire(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              >
                <option value="">-- Choisir un stagiaire --</option>
                {stagiaires.map(stag => (
                  <option key={stag.user_id} value={stag.user_id}>
                    {stag.user_info?.prenom} {stag.user_info?.nom}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={downloadPresenceIndividuelle} 
                variant="outline" 
                className="flex-1"
                disabled={!selectedStagiaire}
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button 
                onClick={downloadPresenceIndividuelleExcel} 
                variant="outline" 
                className="flex-1 border-green-500 text-green-600 hover:bg-green-50"
                disabled={!selectedStagiaire}
              >
                <Download className="h-4 w-4 mr-2" />
                Excel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div 
          className={`bg-white border rounded-lg p-4 cursor-pointer transition-all ${filterStatut === 'all' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}
          onClick={() => setFilterStatut('all')}
        >
          <p className="text-2xl font-bold text-[#0F172A]">{stats.total}</p>
          <p className="text-sm text-slate-600">Total</p>
        </div>
        <div 
          className={`bg-white border rounded-lg p-4 cursor-pointer transition-all ${filterStatut === 'present' ? 'border-green-500 ring-2 ring-green-100' : 'border-slate-200 hover:border-slate-300'}`}
          onClick={() => setFilterStatut('present')}
        >
          <p className="text-2xl font-bold text-green-600">{stats.presents}</p>
          <p className="text-sm text-slate-600">Présents</p>
        </div>
        <div 
          className={`bg-white border rounded-lg p-4 cursor-pointer transition-all ${filterStatut === 'absent' ? 'border-red-500 ring-2 ring-red-100' : 'border-slate-200 hover:border-slate-300'}`}
          onClick={() => setFilterStatut('absent')}
        >
          <p className="text-2xl font-bold text-red-600">{stats.absents}</p>
          <p className="text-sm text-slate-600">Absents</p>
        </div>
        <div 
          className={`bg-white border rounded-lg p-4 cursor-pointer transition-all ${filterStatut === 'retard' ? 'border-orange-500 ring-2 ring-orange-100' : 'border-slate-200 hover:border-slate-300'}`}
          onClick={() => setFilterStatut('retard')}
        >
          <p className="text-2xl font-bold text-orange-600">{stats.retards}</p>
          <p className="text-sm text-slate-600">Retards</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Rechercher un stagiaire..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Liste */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {filteredPresences.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucune présence trouvée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Stagiaire</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Période</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Motif</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredPresences.slice(0, 100).map((presence, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#8DC63F]/20 rounded-full flex items-center justify-center">
                          <span className="text-[#8DC63F] font-semibold text-xs">
                            {presence.stagiaire_info?.prenom?.[0]}{presence.stagiaire_info?.nom?.[0]}
                          </span>
                        </div>
                        <span className="font-medium text-[#0F172A]">
                          {presence.stagiaire_info?.prenom} {presence.stagiaire_info?.nom}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {new Date(presence.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 capitalize">
                      {presence.periode === 'matin' ? 'Matin' : 'Après-midi'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(presence.statut)}
                        {getStatusBadge(presence.statut)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {presence.motif || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponsablePresences;
